import isNil from 'lodash/isNil';
import {
  ICompoundMeasureFieldsFragment,
  ICompoundMeasurePropertiesFieldsFragment,
  IMeasureDisplayFormatFieldsFragment,
  ISimpleMeasureFieldsFragment,
  ISimpleMeasurePropertiesFieldsFragment,
  MeasureType,
} from '../../apollo/graphqlSchema';
import { IQueryMeasureFormula, IRow } from '../../../redux/slices/oldBuilder';
import { defaultRow } from '../../../redux/slices/oldBuilder/defaults';
import { parseCohortRefToBuilder } from '../cohorts';
import { getPercentileValue } from '../percentile';
import { defaultMeasureDisplayFormatInput } from './measureDisplayFormat/utils';

export const parseSimpleMeasure = (
  measure: ISimpleMeasureFieldsFragment,
  measureProperties: ISimpleMeasurePropertiesFieldsFragment | null,
): IRow => {
  const { rollingControl, userAlias, percentile, version } = measure;

  const result: IRow = {
    ...defaultRow(),
    eventConditions: measure.eventConditions,
    fn: measure.function,
    userAliasId: userAlias.aliasId,
  };

  const versionId = version?.versionId;

  if (versionId) {
    result.measureId = versionId;
    result.showMeasure = true;
    result.defaultNamedMeasure = measure;
  }

  if (measureProperties && !isNil(measureProperties?.filter)) {
    result.measureFilter = measureProperties.filter;
  }

  if (!isNil(rollingControl)) {
    result.measureRollingControl = {
      __typename: 'RollingControl',
      function: rollingControl.function,
      window: rollingControl.window,
    };
  }

  if (!isNil(percentile)) {
    // XXX KB-6021 - display percentile number as a whole % instead of a fraction
    result.percentile = getPercentileValue(percentile);
  }

  if (
    measureProperties?.cohortRefs &&
    measureProperties?.cohortRefs?.length > 0
  ) {
    result.cohortFilter = parseCohortRefToBuilder(
      measureProperties.cohortRefs[0],
    );
  }

  return result;
};

// parses a compound measure into a single row. currently only used for data-table
export const parseCompoundMeasure = (
  measure: ICompoundMeasureFieldsFragment,
  measureProperties: ICompoundMeasurePropertiesFieldsFragment | null,
): IRow => {
  const { userAlias, id, version } = measure;

  const result: IRow = {
    ...defaultRow(),
    type: MeasureType.Compound,
    userAliasId: userAlias.aliasId,
    measureId: id,
    isCollapsedMeasure: true,
  };

  const versionId = version?.versionId;

  if (versionId) {
    result.measureId = versionId;
    result.showMeasure = true;
    result.defaultNamedMeasure = measure;
  }

  if (measureProperties?.measures?.length) {
    // Check first measure property for cohort.
    // since the cohort filter is applied to all measures of this
    // compound measure
    const firstMeasureProperty = measureProperties.measures[0];
    if (firstMeasureProperty.cohortRefs?.length) {
      result.cohortFilter = parseCohortRefToBuilder(
        firstMeasureProperty.cohortRefs[0],
      );
    }
  }

  if (measureProperties && !isNil(measureProperties?.resultFilter)) {
    result.measureFilter = measureProperties.resultFilter;
  }

  return result;
};

export const parseMeasureFormula = (
  formula: string | null,
): IQueryMeasureFormula | null => {
  if (!formula) {
    return null;
  }

  return {
    isValid: true,
    expression: formula,
    validationError: null,
  };
};

export const defaultMeasureDisplayFormat: IMeasureDisplayFormatFieldsFragment =
  {
    __typename: 'MeasureDisplayFormat',
    ...defaultMeasureDisplayFormatInput,
  };
